/**
 * Pure helpers for the Attributes analytics section, split out so the
 * aspect-score arithmetic can be unit tested independently of the React component.
 */

/** Aspect scores are in [0, 1]; map to a 0–100 display percentage. */
export function aspectScoreToPercent(score?: number | null): number | undefined {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    return undefined;
  }
  return Math.max(0, Math.min(100, Math.round(score * 100)));
}

/** Coverage is a 0–1 share; map to a whole percentage. */
export function coverageToPercent(coverage?: number | null): number | undefined {
  if (typeof coverage !== 'number' || !Number.isFinite(coverage)) {
    return undefined;
  }
  const percent = coverage >= 0 && coverage <= 1 ? coverage * 100 : coverage;
  return Math.max(0, Math.round(percent));
}

/** Extracts a term key from the loosely-typed `term` field on stats responses. */
export function resolveTermKey(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (value && typeof value === 'object') {
    const record = value as { term?: unknown; key?: unknown; value?: unknown };
    return resolveTermKey(record.term ?? record.key ?? record.value ?? '');
  }
  return '';
}

/** One cell of a stats matrix row: a term on the opposite axis plus its aspect score. */
export interface MatrixCellLike {
  term?: unknown;
  aspectScore?: number | null;
}

/** One row of a stats matrix: a term on this axis plus its cells. */
export interface MatrixRowLike {
  term?: unknown;
  cols?: MatrixCellLike[] | null;
}

/**
 * Mean of a matrix row's measured aspect scores, as a 0–100 percentage.
 *
 * Cells with no `aspectScore` are excluded from the denominator rather than counted as zero — an
 * entity that was never scored on an attribute must not drag the mean down.
 */
export function meanAspectPercent(cells: readonly MatrixCellLike[] | null | undefined): number | undefined {
  const percentages = (cells || [])
    .map((cell) => aspectScoreToPercent(cell.aspectScore))
    .filter((percent): percent is number => percent !== undefined);

  if (!percentages.length) {
    return undefined;
  }

  return Math.round(percentages.reduce((total, percent) => total + percent, 0) / percentages.length);
}

/**
 * Indexes a stats matrix as `attributeKey -> mean aspect score across that row's cells`.
 *
 * The caller decides which population a matrix covers by choosing its x-axis taxonomy: passing
 * the own-brand taxonomy yields "your" score, passing the competitor taxonomy yields the
 * competitor average. Because the two come from two separate requests over two disjoint term
 * sets, they cannot silently collapse onto the same number the way `total.terms[].aspectScore`
 * and `total.terms[].averageAspectScore` do (those are an unweighted and a record-count-weighted
 * mean over one and the same population).
 */
export function buildAttributeScoreIndex(rows: readonly MatrixRowLike[] | null | undefined): Map<string, number> {
  const index = new Map<string, number>();

  for (const row of rows || []) {
    const attributeKey = resolveTermKey(row.term);
    if (!attributeKey) {
      continue;
    }
    const score = meanAspectPercent(row.cols);
    if (score !== undefined) {
      index.set(attributeKey, score);
    }
  }

  return index;
}

/** One attribute row of the leaderboard. */
export interface LeaderboardRow {
  termKey: string;
  /** Number of answers that discussed this attribute — the literal "Mentions" figure. */
  count: number;
  /** Those answers as a share of every answer in the window. */
  coverage?: number;
  ownScore?: number;
  averageScore?: number;
}

/** The per-attribute totals row as the stats endpoint returns it. */
export interface AttributeTotalTermLike {
  term?: unknown;
  count?: number | null;
  coverage?: number | null;
}

/**
 * Builds the leaderboard from the per-attribute totals, attaching scores from the two entity
 * matrices when they are available.
 *
 * Counts and coverage come from the totals endpoint alone, so they keep rendering even when a
 * matrix request fails or returns nothing — coupling them to the matrices is what previously
 * blanked the whole module. Scores are looked up per attribute and stay undefined when unmeasured.
 */
export function buildLeaderboardRows(
  terms: readonly AttributeTotalTermLike[] | null | undefined,
  ownScoreByAttribute: Map<string, number>,
  competitorScoreByAttribute: Map<string, number>
): LeaderboardRow[] {
  return (terms || [])
    .map((term) => {
      const termKey = resolveTermKey(term.term);
      return {
        termKey,
        count: typeof term.count === 'number' && Number.isFinite(term.count) ? term.count : 0,
        coverage: coverageToPercent(term.coverage),
        ownScore: ownScoreByAttribute.get(termKey),
        averageScore: competitorScoreByAttribute.get(termKey),
      };
    })
    .filter((row) => !!row.termKey)
    .sort((a, b) => b.count - a.count || (b.coverage ?? 0) - (a.coverage ?? 0));
}

/** How a score sits against its reference. `unknown` when either side was not measured. */
export type ScoreComparison = 'ahead' | 'level' | 'behind' | 'unknown';

/**
 * Compares a score against a reference. `tolerance` (in percentage points) is the band within
 * which the two count as level, so a 1-point wobble does not flip the colour.
 */
export function compareScores(value?: number, reference?: number, tolerance = 1): ScoreComparison {
  if (value === undefined || reference === undefined) {
    return 'unknown';
  }
  const delta = value - reference;
  if (Math.abs(delta) <= tolerance) {
    return 'level';
  }
  return delta > 0 ? 'ahead' : 'behind';
}

/**
 * The three labels below all count a row's answers out of the window's total, so they share one
 * rule about what that total is.
 *
 * `analyzedPromptRuns` is the window's ANALYZED PROMPT RUNS. Every `LeaderboardRow.count` is a
 * distinct-run count server-side, so this is the only figure a row's numerator can honestly be
 * counted out of — and it is literally the number the server divided by to produce the same row's
 * `coverage`. The distinct-PROMPT count (`/total`'s `count`) is a smaller number in a different
 * unit; pairing the two printed sentences whose numerator exceeded their denominator.
 *
 * undefined, or a non-positive value, means no denominator was reported — a Sync Core older than
 * the release that added the field answers without one. Each label then states its numerator alone
 * rather than dividing by a number it never received.
 *
 * The parameters keep the prompt-run vocabulary the API uses; the rendered copy keeps "answers",
 * which is what this surface has always called one run of one prompt against one engine.
 */

/**
 * "1 answer" / "5 answers", and the window total when it is known. Coverage is deliberately not
 * shown as a percentage here: one answer out of a few hundred rounds to 0%, which read as "never
 * mentioned" when the true figure was "mentioned once".
 */
export function formatAnswerCountSuffix(count: number, analyzedPromptRuns?: number): string {
  const noun = count === 1 ? 'answer' : 'answers';
  return analyzedPromptRuns !== undefined && analyzedPromptRuns > 0 ? ` of ${analyzedPromptRuns} ${noun}` : ` ${noun}`;
}

/**
 * The one-sentence explanation of a leaderboard row — what was measured, how often, and how you
 * compare. Named parts are omitted rather than guessed when they were not measured.
 */
export function leaderboardRowSentence(row: LeaderboardRow, analyzedPromptRuns?: number): string {
  const share =
    analyzedPromptRuns && analyzedPromptRuns > 0
      ? (() => {
          const percent = (row.count / analyzedPromptRuns) * 100;
          return percent > 0 && percent < 1 ? `${percent.toFixed(1)}%` : `${Math.round(percent)}%`;
        })()
      : undefined;

  const where = share
    ? `${row.count} of ${analyzedPromptRuns} answers (${share})`
    : `${row.count} ${row.count === 1 ? 'answer' : 'answers'}`;
  const scores =
    row.ownScore !== undefined && row.averageScore !== undefined
      ? `your average score is ${row.ownScore} vs a competitor average of ${row.averageScore}`
      : row.ownScore !== undefined
        ? `your average score is ${row.ownScore}; no competitor was scored on it`
        : 'no score was measured for you on it';

  return `AI evaluated you on this attribute in ${where}; ${scores}. Click to see those answers.`;
}

/** "Discussed in 12 answers of 290 in this window (4%)" — the Mentions column's hover reading. */
export function mentionsTitle(row: LeaderboardRow, analyzedPromptRuns?: number): string {
  const base = `Discussed in ${row.count} ${row.count === 1 ? 'answer' : 'answers'}`;
  if (analyzedPromptRuns === undefined || analyzedPromptRuns <= 0) {
    return base;
  }
  // One decimal keeps a 1-in-N share visible instead of rounding it away to 0%.
  const share = (row.count / analyzedPromptRuns) * 100;
  const rendered = share > 0 && share < 1 ? share.toFixed(1) : String(Math.round(share));
  return `${base} of ${analyzedPromptRuns} in this window (${rendered}%)`;
}
